import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, DollarSign, Wallet, Percent, Clock, Briefcase, Gauge } from "lucide-react";
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthsAgo = (m) => {
  const d = new Date();
  d.setMonth(d.getMonth() - m);
  return d;
};
const fyStartYear = (d = new Date()) => (d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1);

export const PRESETS = {
  this_month: { label: "This month", range: () => [iso(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), iso(new Date())] },
  last_3: { label: "Last 3 months", range: () => [iso(monthsAgo(3)), iso(new Date())] },
  last_6: { label: "Last 6 months", range: () => [iso(monthsAgo(6)), iso(new Date())] },
  last_12: { label: "Last 12 months", range: () => [iso(monthsAgo(12)), iso(new Date())] },
  this_fy: { label: "This financial year", range: () => [`${fyStartYear()}-07-01`, iso(new Date())] },
  last_fy: { label: "Last financial year", range: () => [`${fyStartYear() - 1}-07-01`, `${fyStartYear()}-06-30`] },
  all: { label: "All time", range: () => [null, null] },
  custom: { label: "Custom range", range: () => null },
};

const aud = (v, digits = 0) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v || 0);
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });

const Kpi = ({ icon: Icon, label, value, hint, testId, tone = "text-gray-900", className = "" }) => (
  <div className={`rounded-lg border border-gray-100 bg-white p-4 ${className}`} data-testid={testId}>
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className={`mt-1.5 font-mono text-xl font-semibold ${tone}`} data-testid={`${testId}-value`}>{value}</div>
    {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
  </div>
);

const KpiGrid = ({ t }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bp-kpis">
    <Kpi icon={DollarSign} label="Total revenue" value={aud(t.revenue)} hint="Sell price ex GST" testId="bp-revenue" />
    <Kpi icon={Wallet} label="Total true cost" value={aud(t.cost)} hint="Production true cost" testId="bp-cost" />
    <Kpi icon={TrendingUp} label="Total true profit" value={aud(t.profit)} tone={t.profit < 0 ? "text-red-600" : "text-emerald-700"} testId="bp-profit" />
    <Kpi icon={Percent} label="Average margin" value={`${t.margin_percent}%`} hint="Total profit ÷ total revenue" testId="bp-margin" />
    <Kpi icon={Gauge} label="Average margin per hour" value={`${aud(t.margin_per_hour, 2)}/hr`} hint="Total profit ÷ total hours" testId="bp-margin-per-hour" />
    <Kpi icon={Clock} label="Est. production hours" value={`${t.hours.toLocaleString("en-AU")} hrs`} testId="bp-hours" />
    <Kpi icon={Briefcase} label="Jobs included" value={t.jobs} testId="bp-jobs" className="col-span-2 md:col-span-1" />
  </div>
);

const ProfitTrend = ({ monthly }) => {
  if (!monthly.length) return <div className="text-sm text-gray-400 text-center py-12" data-testid="bp-chart-empty">No qualifying jobs in this period.</div>;
  const data = monthly.map((m) => ({ ...m, label: monthLabel(m.month), margin_percent: m.jobs ? m.margin_percent : null }));
  return (
    <div className="h-72" data-testid="bp-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="p" tick={{ fontSize: 11 }} tickFormatter={(v) => aud(v)} width={70} />
          <YAxis yAxisId="m" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} width={45} />
          <Tooltip
            contentStyle={{ fontSize: 12 }}
            formatter={(v, name) => (name === "Margin %" ? (v === null ? "—" : `${v}%`) : aud(v))}
            labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.jobs ?? 0} jobs`}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="p" dataKey="profit" name="True profit" fill="#059669" maxBarSize={56} radius={[3, 3, 0, 0]}>
            {data.map((d) => <Cell key={d.month} fill={d.profit < 0 ? "#dc2626" : "#059669"} />)}
          </Bar>
          <Line yAxisId="p" type="linear" dataKey="revenue" name="Revenue" stroke="#94a3b8" strokeWidth={1.5} dot={false} />
          <Line yAxisId="m" type="linear" dataKey="margin_percent" name="Margin %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const RangePicker = ({ preset, setPreset, custom, setCustom, onRefresh, loading, idPrefix = "bp" }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Select value={preset} onValueChange={setPreset}>
      <SelectTrigger className="w-48 h-9 text-sm" data-testid={`${idPrefix}-period-select`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {Object.entries(PRESETS).map(([k, p]) => <SelectItem key={k} value={k} data-testid={`${idPrefix}-period-${k}`}>{p.label}</SelectItem>)}
      </SelectContent>
    </Select>
    {preset === "custom" && (
      <>
        <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="h-9 w-[150px] text-sm" data-testid={`${idPrefix}-custom-from`} />
        <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="h-9 w-[150px] text-sm" data-testid={`${idPrefix}-custom-to`} />
      </>
    )}
    <Button variant="outline" size="sm" onClick={onRefresh} className="h-9" data-testid={`${idPrefix}-refresh-btn`}>
      <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
    </Button>
  </div>
);

export default function BusinessProfitability() {
  const [preset, setPreset] = useState("last_12");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => (preset === "custom" ? [custom.from || null, custom.to || null] : PRESETS[preset].range()), [preset, custom]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = {};
      if (range[0]) params.start_date = range[0];
      if (range[1]) params.end_date = range[1];
      const res = await axios.get(`${API}/analytics/business-profitability`, { params, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load business profitability");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range[0], range[1]]);

  return (
    <div className="p-6" data-testid="business-profitability">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" /> Business Profitability
          </h2>
          <p className="text-sm text-gray-500 mt-1">Approved, In Production, Complete and Paid jobs, dated by quote date.</p>
        </div>
        <RangePicker preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} onRefresh={load} loading={loading} />
      </div>
      {loading && !data ? (
        <div className="text-center py-12 text-gray-400"><RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />Loading...</div>
      ) : data ? (
        <div className="space-y-6">
          <KpiGrid t={data.totals} />
          {data.skipped_without_pricing > 0 && (
            <p className="text-xs text-amber-700" data-testid="bp-skipped-note">
              {data.skipped_without_pricing} job{data.skipped_without_pricing === 1 ? "" : "s"} in this period left out because no pricing has been saved on them.
            </p>
          )}
          {data.estimated_jobs?.length > 0 && (
            <div className="text-xs text-gray-600 flex flex-wrap items-center gap-1.5" data-testid="bp-estimated-note">
              <span>True cost recalculated from saved quote data (estimated) for:</span>
              {data.estimated_jobs.map((j) => (
                <span key={j.id} className="rounded bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-0.5 font-mono" title={j.job_name} data-testid={`bp-estimated-job-${j.job_number || j.id}`}>
                  {j.job_number || j.job_name} · Estimated
                </span>
              ))}
            </div>
          )}
          <div className="rounded-lg border border-gray-100 bg-white p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Profit trend by month</h3>
            <ProfitTrend monthly={data.monthly} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
